/**
 * Cross-Framework Intelligence Dashboard
 * 
 * Powered by live /api/v1/cfi/* endpoints.
 * Shows 47 topics × 14 frameworks, 14×14 overlap matrix,
 * 45 KPIs, CSRD-CSDDD intersection, auto-link, and proof points.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GitMerge, Search, Filter, ChevronDown, ChevronRight,
  Layers, Target, Shield, BarChart3, TrendingUp, Zap,
  ArrowRight, CheckCircle2, AlertCircle, XCircle, Link2,
  Activity, FileText, Eye, Loader2, Info
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const FLAG_COLORS = {
  R: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Required' },
  C: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Conditional' },
  V: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', label: 'Voluntary' },
  '-': { bg: 'bg-[#1A1A1A]', text: 'text-[#333]', border: 'border-transparent', label: 'N/A' },
};

const CATEGORY_COLORS = {
  'E:': '#10b981', 'S:': '#3b82f6', 'G:': '#8b5cf6', 'Cross': '#f59e0b',
};

function getCatColor(cat) {
  for (const [prefix, color] of Object.entries(CATEGORY_COLORS)) {
    if (cat.startsWith(prefix)) return color;
  }
  return '#6b7a96';
}

const Pill = ({ color, children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${className}`}
    style={{ background: color + '18', color, border: `1px solid ${color}30` }}>
    {children}
  </span>
);

const TabBtn = ({ active, onClick, children, icon: Icon }) => (
  <button onClick={onClick} data-testid={`tab-${children.toString().toLowerCase().replace(/\s+/g, '-')}`}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
      active ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
    }`}>
    {Icon && <Icon size={14} />}{children}
  </button>
);

// ═══════════════════════════════════════════════════════════════
// STATS HEADER
// ═══════════════════════════════════════════════════════════════

function StatsHeader({ stats }) {
  if (!stats) return null;
  const items = [
    { label: 'Mapping Decisions', value: stats.total_mapping_decisions, icon: GitMerge, color: '#6366f1' },
    { label: 'Topics', value: stats.total_topics, icon: Layers, color: '#10b981' },
    { label: 'KPIs Tracked', value: stats.total_kpis, icon: Activity, color: '#3b82f6' },
    { label: 'Frameworks', value: stats.total_frameworks, icon: Shield, color: '#f59e0b' },
    { label: 'Avg FW/Topic', value: stats.avg_frameworks_per_topic, icon: TrendingUp, color: '#06b6d4' },
    { label: 'CSRD-CSDDD Overlap', value: `${stats.csrd_csddd_avg_overlap}%`, icon: Link2, color: '#ec4899' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cfi-stats-header">
      {items.map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-3 hover:border-[#2F2F2F] transition-colors">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={14} style={{ color }} />
            <span className="text-[10px] text-[#6B6B6B] uppercase tracking-wider">{label}</span>
          </div>
          <div className="text-xl font-bold text-white">{value}</div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 1: FRAMEWORK MATRIX (47 × 14)
// ═══════════════════════════════════════════════════════════════

function MatrixTab({ data, search }) {
  const [expanded, setExpanded] = useState(null);
  if (!data) return <LoadingPlaceholder />;

  const topics = data.topics.filter(t =>
    !search || t.topic.toLowerCase().includes(search.toLowerCase()) || t.category.toLowerCase().includes(search.toLowerCase())
  );
  const fws = data.frameworks || [];

  const grouped = useMemo(() => {
    const g = {};
    topics.forEach(t => { (g[t.category] = g[t.category] || []).push(t); });
    return g;
  }, [topics]);

  return (
    <div className="space-y-2" data-testid="cfi-matrix-tab">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex gap-3 text-[10px]">
          {Object.entries(FLAG_COLORS).filter(([k]) => k !== '-').map(([flag, cfg]) => (
            <span key={flag} className={`px-2 py-0.5 rounded ${cfg.bg} ${cfg.text}`}>
              {flag} = {cfg.label}
            </span>
          ))}
          <span className="px-2 py-0.5 rounded bg-[#1A1A1A] text-[#444]">- = Not Applicable</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#1F1F1F]">
        <table className="w-full border-collapse text-[11px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#0D0D0D]">
              <th className="sticky left-0 z-20 bg-[#0D0D0D] text-left p-2 min-w-[200px] text-[#6B6B6B] font-semibold border-b border-r border-[#1F1F1F]">
                Topic ({topics.length})
              </th>
              {fws.map(fw => (
                <th key={fw} className="p-2 text-center text-[#6B6B6B] font-semibold border-b border-l border-[#1F1F1F] min-w-[60px] whitespace-nowrap">
                  <span className="block text-[9px]">{fw.replace('CSRD/', '').replace(' (S1/S2)', '')}</span>
                </th>
              ))}
              <th className="p-2 text-center text-[#6B6B6B] font-semibold border-b border-l border-[#1F1F1F] min-w-[50px]">FW#</th>
              <th className="p-2 text-center text-[#6B6B6B] font-semibold border-b border-l border-[#1F1F1F] min-w-[55px]">Reuse</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([cat, items]) => (
              <React.Fragment key={cat}>
                <tr className="bg-[#0A0A0A]">
                  <td colSpan={fws.length + 3} className="p-1.5 border-b border-[#1F1F1F]">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: getCatColor(cat) }}>
                      {cat} ({items.length})
                    </span>
                  </td>
                </tr>
                {items.map((topic, idx) => (
                  <React.Fragment key={topic.topic}>
                    <tr className={`${idx % 2 === 0 ? 'bg-[#111111]' : 'bg-[#0D0D0D]'} hover:bg-[#1A1A1A] cursor-pointer transition-colors`}
                      onClick={() => setExpanded(expanded === topic.topic ? null : topic.topic)}>
                      <td className="sticky left-0 z-10 bg-inherit p-2 border-r border-[#1F1F1F] font-medium text-gray-300">
                        <div className="flex items-center gap-1">
                          {expanded === topic.topic ? <ChevronDown size={10} className="text-[#6B6B6B]" /> : <ChevronRight size={10} className="text-[#6B6B6B]" />}
                          <span className="truncate max-w-[180px]">{topic.topic}</span>
                        </div>
                      </td>
                      {fws.map(fw => {
                        const flag = topic.frameworks[fw] || '-';
                        const cfg = FLAG_COLORS[flag] || FLAG_COLORS['-'];
                        return (
                          <td key={fw} className={`p-1 text-center border-l border-[#1F1F1F]`}>
                            <span className={`inline-block w-6 h-5 leading-5 rounded text-[9px] font-bold ${cfg.bg} ${cfg.text}`}>
                              {flag}
                            </span>
                          </td>
                        );
                      })}
                      <td className="p-2 text-center border-l border-[#1F1F1F] text-cyan-400 font-bold">{topic.fw_count}</td>
                      <td className="p-2 text-center border-l border-[#1F1F1F]">
                        <span className={`text-[10px] font-bold ${parseInt(topic.reuse_pct) >= 80 ? 'text-emerald-400' : parseInt(topic.reuse_pct) >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                          {topic.reuse_pct}
                        </span>
                      </td>
                    </tr>
                    {expanded === topic.topic && (
                      <tr className="bg-[#0A0A0A]">
                        <td colSpan={fws.length + 3} className="p-3 border-b border-[#1F1F1F]">
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mb-1">Intelligence Note</p>
                              <p className="text-xs text-gray-300">{topic.intelligence}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] text-[#6B6B6B]">Frameworks: {topic.fw_count}/14</p>
                              <p className="text-[10px] text-[#6B6B6B]">Reuse: {topic.reuse_pct}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 2: OVERLAP MATRIX (14 × 14)
// ═══════════════════════════════════════════════════════════════

function OverlapTab({ data }) {
  const [selectedFw, setSelectedFw] = useState(null);
  if (!data) return <LoadingPlaceholder />;

  const fws = data.frameworks || [];

  const getHeatColor = (pct) => {
    const v = parseInt(pct) || 0;
    if (v >= 70) return 'bg-emerald-500/40 text-emerald-300';
    if (v >= 40) return 'bg-amber-500/30 text-amber-300';
    if (v >= 15) return 'bg-blue-500/20 text-blue-300';
    if (v > 0) return 'bg-[#1F1F1F] text-[#555]';
    return 'bg-[#0D0D0D] text-[#333]';
  };

  return (
    <div className="space-y-4" data-testid="cfi-overlap-tab">
      <p className="text-xs text-[#6B6B6B]">
        Each cell shows: <span className="text-gray-300">"% of ROW framework requirements satisfiable by evidence from COLUMN framework."</span>
        <br />Higher values = more evidence reuse opportunity.
      </p>

      <div className="overflow-x-auto rounded-xl border border-[#1F1F1F]">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="bg-[#0D0D0D]">
              <th className="sticky left-0 z-10 bg-[#0D0D0D] p-2 text-left text-[#6B6B6B] font-semibold border-b border-r border-[#1F1F1F] min-w-[100px]">
                Row \ Col
              </th>
              {fws.map(fw => (
                <th key={fw} className="p-1.5 text-center text-[#6B6B6B] font-semibold border-b border-l border-[#1F1F1F] min-w-[55px]"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 80 }}>
                  {fw.replace('CSRD/', '').replace(' (S1/S2)', '')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fws.map((row, ri) => (
              <tr key={row} className={`${ri % 2 === 0 ? 'bg-[#111111]' : 'bg-[#0D0D0D]'} hover:bg-[#1A1A1A] transition-colors cursor-pointer`}
                onClick={() => setSelectedFw(selectedFw === row ? null : row)}>
                <td className="sticky left-0 z-10 bg-inherit p-2 font-semibold text-gray-300 border-r border-[#1F1F1F] whitespace-nowrap">
                  {row.replace('CSRD/', '').replace(' (S1/S2)', '')}
                </td>
                {fws.map(col => {
                  const val = data.matrix[row]?.[col] || '0%';
                  const isDiag = row === col;
                  return (
                    <td key={col} className={`p-1 text-center border-l border-[#1F1F1F] ${isDiag ? 'bg-indigo-500/20' : getHeatColor(val)}`}>
                      <span className={`font-bold ${isDiag ? 'text-indigo-400' : ''}`}>
                        {isDiag ? '-' : val}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedFw && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Shield size={14} className="text-indigo-400" />
            {selectedFw} — Overlap Detail
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {fws.filter(f => f !== selectedFw).map(fw => {
              const gives = data.matrix[selectedFw]?.[fw] || '0%';
              const gets = data.matrix[fw]?.[selectedFw] || '0%';
              return (
                <div key={fw} className="bg-[#0D0D0D] rounded-lg p-2 text-xs">
                  <span className="font-semibold text-gray-300">{fw.replace('CSRD/', '').replace(' (S1/S2)', '')}</span>
                  <div className="flex justify-between mt-1">
                    <span className="text-emerald-400">gives {gives}</span>
                    <span className="text-cyan-400">gets {gets}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 3: KPI INTELLIGENCE
// ═══════════════════════════════════════════════════════════════

function KPITab({ data, search }) {
  const [expandedKpi, setExpandedKpi] = useState(null);
  if (!data) return <LoadingPlaceholder />;

  const kpis = data.kpis.filter(k =>
    !search || k.name.toLowerCase().includes(search.toLowerCase()) || k.category.toLowerCase().includes(search.toLowerCase())
  );
  const cats = data.categories || [];

  return (
    <div className="space-y-3" data-testid="cfi-kpi-tab">
      <div className="flex flex-wrap gap-2 mb-2">
        {cats.map(c => (
          <Pill key={c} color={getCatColor(c)}>{c} ({data.kpis.filter(k => k.category === c).length})</Pill>
        ))}
      </div>

      <div className="space-y-1.5">
        {kpis.map((kpi, idx) => (
          <div key={idx} className="bg-[#111111] border border-[#1F1F1F] rounded-lg overflow-hidden hover:border-[#2F2F2F] transition-colors">
            <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpandedKpi(expandedKpi === idx ? null : idx)}>
              <div className="w-2 h-2 rounded-full" style={{ background: getCatColor(kpi.category) }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white truncate">{kpi.name}</p>
                <p className="text-[10px] text-[#6B6B6B]">{kpi.esrs_ref} | {kpi.unit} | {kpi.frequency}</p>
              </div>
              <Pill color="#06b6d4">{kpi.fw_count} FW</Pill>
              {expandedKpi === idx ? <ChevronDown size={12} className="text-[#6B6B6B]" /> : <ChevronRight size={12} className="text-[#6B6B6B]" />}
            </div>
            {expandedKpi === idx && kpi.framework_refs && (
              <div className="border-t border-[#1F1F1F] p-3 bg-[#0A0A0A]">
                <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mb-2">Framework References</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(kpi.framework_refs).map(([fw, ref]) => (
                    <span key={fw} className="px-2 py-1 bg-[#1F1F1F] rounded text-[10px]">
                      <span className="text-gray-400 font-semibold">{fw}:</span>{' '}
                      <span className="text-white">{ref}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 4: CSRD-CSDDD INTERSECTION
// ═══════════════════════════════════════════════════════════════

function IntersectionTab({ data }) {
  const [expanded, setExpanded] = useState(null);
  if (!data) return <LoadingPlaceholder />;

  return (
    <div className="space-y-3" data-testid="cfi-intersection-tab">
      <div className="flex items-center gap-3 mb-2">
        <Pill color="#ec4899">Avg Overlap: {data.avg_overlap}%</Pill>
        <Pill color="#6366f1">{data.total} Requirement Pairs</Pill>
      </div>

      {data.intersections.map((pair, idx) => (
        <div key={idx} className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden hover:border-[#2F2F2F] transition-colors">
          <div className="p-3 cursor-pointer" onClick={() => setExpanded(expanded === idx ? null : idx)}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[9px] font-bold rounded">CSRD</span>
                  <span className="text-xs font-semibold text-white">{pair.csrd_req}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[9px] font-bold rounded">CSDDD</span>
                  <span className="text-xs text-gray-400">{pair.csddd_req}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={`text-lg font-bold ${parseInt(pair.overlap_pct) >= 70 ? 'text-emerald-400' : parseInt(pair.overlap_pct) >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  {pair.overlap_pct}
                </div>
                <div className="text-[9px] text-[#6B6B6B]">overlap</div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[9px] text-[#6B6B6B]">{pair.esrs_ref}</span>
              <ArrowRight size={8} className="text-[#444]" />
              <span className="text-[9px] text-[#6B6B6B]">{pair.csddd_article}</span>
              <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-[#1F1F1F] rounded text-[#6B6B6B]">{pair.module}</span>
            </div>
          </div>
          {expanded === idx && (
            <div className="border-t border-[#1F1F1F] p-3 bg-[#0A0A0A] space-y-2">
              <div>
                <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mb-1">Shared Evidence</p>
                <p className="text-xs text-gray-300">{pair.evidence_shared}</p>
              </div>
              <div>
                <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mb-1">Proof Point</p>
                <p className="text-xs text-emerald-400">{pair.proof_point}</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 5: AUTO-LINK & PROOF POINTS
// ═══════════════════════════════════════════════════════════════

function AutoLinkTab({ proofPoints, reuse }) {
  const [topic, setTopic] = useState('');
  const [autoLinkResult, setAutoLinkResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runAutoLink = useCallback(async () => {
    if (!topic.trim()) return;
    setLoading(true);
    const res = await apiFetch('/cfi/auto-link', { method: 'POST', body: JSON.stringify({ evidence_topic: topic, source_framework: 'CSRD/ESRS' }) });
    if (res && !res._error) setAutoLinkResult(res);
    setLoading(false);
  }, [topic]);

  return (
    <div className="space-y-6" data-testid="cfi-autolink-tab">
      {/* Auto-Link */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <Zap size={14} className="text-amber-400" /> Auto-Link Evidence
        </h3>
        <div className="flex gap-2 mb-3">
          <input type="text" value={topic} onChange={e => setTopic(e.target.value)} placeholder="Enter topic (e.g. Scope 1, Water, Board)..."
            onKeyDown={e => e.key === 'Enter' && runAutoLink()}
            data-testid="autolink-topic-input"
            className="flex-1 px-3 py-2 bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg text-sm text-white placeholder:text-[#555] focus:border-indigo-500/50 focus:outline-none" />
          <button onClick={runAutoLink} disabled={loading} data-testid="autolink-submit-btn"
            className="px-4 py-2 bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 rounded-lg text-xs font-bold hover:bg-indigo-500/30 transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Auto-Link'}
          </button>
        </div>

        {autoLinkResult && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Pill color="#10b981">{autoLinkResult.total_links} frameworks linked</Pill>
              <Pill color="#3b82f6">{autoLinkResult.high_confidence} high confidence</Pill>
              <Pill color="#f59e0b">Reuse: {autoLinkResult.reuse_pct}</Pill>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
              {autoLinkResult.auto_linked?.map((link, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-[#0D0D0D] rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded text-center text-[9px] font-bold leading-5 ${FLAG_COLORS[link.flag]?.bg} ${FLAG_COLORS[link.flag]?.text}`}>{link.flag}</span>
                    <span className="text-xs text-gray-300 truncate max-w-[150px]">{link.framework}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#6B6B6B]">{link.overlap_with_source}</span>
                    <span className={`text-xs font-bold ${link.confidence >= 0.7 ? 'text-emerald-400' : link.confidence >= 0.4 ? 'text-amber-400' : 'text-red-400'}`}>
                      {Math.round(link.confidence * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reuse Analytics */}
      {reuse && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-emerald-400" /> Reuse Analytics
          </h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-[#0D0D0D] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-400">{reuse.avg_reuse_pct}%</div>
              <div className="text-[10px] text-[#6B6B6B]">Avg Reuse</div>
            </div>
            <div className="bg-[#0D0D0D] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-cyan-400">{reuse.avg_fw_per_topic}</div>
              <div className="text-[10px] text-[#6B6B6B]">Avg FW/Topic</div>
            </div>
            <div className="bg-[#0D0D0D] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-white">{reuse.total_topics}</div>
              <div className="text-[10px] text-[#6B6B6B]">Total Topics</div>
            </div>
          </div>
          {reuse.reuse_buckets && (
            <div className="space-y-2">
              {Object.entries(reuse.reuse_buckets).map(([bucket, info]) => {
                const color = bucket === 'high' ? '#10b981' : bucket === 'medium' ? '#f59e0b' : '#ef4444';
                return (
                  <div key={bucket} className="flex items-center gap-3 p-2 bg-[#0D0D0D] rounded-lg">
                    <span className="text-xs font-bold uppercase w-16" style={{ color }}>{bucket}</span>
                    <div className="flex-1 h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${(info.count / reuse.total_topics) * 100}%`, background: color }} />
                    </div>
                    <span className="text-xs text-gray-400">{info.count} topics</span>
                    <span className="text-[10px] text-[#6B6B6B]">avg {info.avg_fw_count} FW</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Proof Points */}
      {proofPoints?.proof_points && (
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Target size={14} className="text-purple-400" /> Investor Proof Points
          </h3>
          <div className="space-y-2">
            {proofPoints.proof_points.map((pp, i) => (
              <div key={i} className="p-3 bg-[#0D0D0D] rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-white">{pp.category}</span>
                  <span className="px-2 py-0.5 bg-[#1F1F1F] rounded text-[9px] text-[#6B6B6B]">{pp.data_source}</span>
                </div>
                <p className="text-sm font-bold text-emerald-400">{pp.metric}</p>
                <p className="text-[10px] text-gray-500 mt-1">{pp.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LOADING PLACEHOLDER
// ═══════════════════════════════════════════════════════════════

function LoadingPlaceholder() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-8 w-8 text-indigo-400 animate-spin" />
      <span className="ml-3 text-sm text-[#6B6B6B]">Loading intelligence data...</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const CrossFrameworkMapping = () => {
  const [tab, setTab] = useState('matrix');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [overlap, setOverlap] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [intersection, setIntersection] = useState(null);
  const [proofPoints, setProofPoints] = useState(null);
  const [reuse, setReuse] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [s, m, o, k, i, pp, r] = await Promise.all([
      apiFetch('/cfi/stats'),
      apiFetch('/cfi/matrix'),
      apiFetch('/cfi/overlap'),
      apiFetch('/cfi/kpis'),
      apiFetch('/cfi/csrd-csddd'),
      apiFetch('/cfi/proof-points'),
      apiFetch('/cfi/reuse-analytics'),
    ]);
    if (s && !s._error) setStats(s);
    if (m && !m._error) setMatrix(m);
    if (o && !o._error) setOverlap(o);
    if (k && !k._error) setKpis(k);
    if (i && !i._error) setIntersection(i);
    if (pp && !pp._error) setProofPoints(pp);
    if (r && !r._error) setReuse(r);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 lg:p-6" data-testid="cross-framework-mapping">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-500/10 rounded-xl border border-indigo-500/30">
            <GitMerge className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-lg lg:text-xl font-bold text-white">Cross-Framework Intelligence</h1>
            <p className="text-xs text-[#6B6B6B]">
              {stats ? `${stats.total_mapping_decisions} mapping decisions across ${stats.total_frameworks} frameworks` : 'Loading...'}
            </p>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B6B6B]" />
          <input type="text" placeholder="Search topics, KPIs..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="cfi-search-input"
            className="pl-9 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-xs text-white placeholder:text-[#555] focus:border-indigo-500/50 focus:outline-none w-56" />
        </div>
      </div>

      {/* Stats */}
      {!loading && <StatsHeader stats={stats} />}

      {/* Tabs */}
      <div className="flex items-center gap-1 mt-5 mb-4 overflow-x-auto pb-1">
        <TabBtn active={tab === 'matrix'} onClick={() => setTab('matrix')} icon={Layers}>Matrix (47×14)</TabBtn>
        <TabBtn active={tab === 'overlap'} onClick={() => setTab('overlap')} icon={BarChart3}>Overlap (14×14)</TabBtn>
        <TabBtn active={tab === 'kpis'} onClick={() => setTab('kpis')} icon={Activity}>KPIs ({kpis?.total || 45})</TabBtn>
        <TabBtn active={tab === 'csrd-csddd'} onClick={() => setTab('csrd-csddd')} icon={Link2}>CSRD-CSDDD</TabBtn>
        <TabBtn active={tab === 'autolink'} onClick={() => setTab('autolink')} icon={Zap}>Auto-Link & Proof</TabBtn>
      </div>

      {/* Content */}
      {loading ? <LoadingPlaceholder /> : (
        <>
          {tab === 'matrix' && <MatrixTab data={matrix} search={search} />}
          {tab === 'overlap' && <OverlapTab data={overlap} />}
          {tab === 'kpis' && <KPITab data={kpis} search={search} />}
          {tab === 'csrd-csddd' && <IntersectionTab data={intersection} />}
          {tab === 'autolink' && <AutoLinkTab proofPoints={proofPoints} reuse={reuse} />}
        </>
      )}
    </div>
  );
};

export default CrossFrameworkMapping;
